import React from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import SPAButton from '@/components/admin/SysadminSPA/shared/SPAButton';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import { useSPAToast } from '@/components/admin/SysadminSPA/shared/SPAToast';
import type { SlideRow } from './types';

interface DeleteSlideDialogProps {
  onDeleted: (slide: SlideRow) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  slide: SlideRow;
}

// Deletes through the plato_api slide endpoint, which answers 405 when the model refuses (a slide
// with images that has been copied), so the row only disappears once the server agrees.
const DeleteSlideDialog = ({
  onDeleted, onOpenChange, open, slide,
}: DeleteSlideDialogProps) => {
  const spaToast = useSPAToast();
  const remove = useMutation({
    mutationFn: () => Axios.delete(Routes.plato_api_slide_path(slide.id)),
    onSuccess: () => {
      onOpenChange(false);
      onDeleted(slide);
      spaToast.success(`Deleted ${slide.title}.`);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (!next) remove.reset();
    onOpenChange(next);
  };

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Content size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Delete {slide.title}?</SPADialog.Title>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3">
          <SPADialog.Description>
            This also deletes the slide&apos;s images and games. It can&apos;t be undone.
          </SPADialog.Description>
          {remove.isError && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error" role="alert">The slide could not be deleted. Try again.</p>
          )}
        </SPADialog.Body>
        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton>Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton disabled={remove.isLoading} variant="danger" onClick={() => remove.mutate()}>Delete</SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default DeleteSlideDialog;
